import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DiscordConnection, DiscordGuildChannel } from '@playerbase/types';

import { fetchDiscordConnectionForGame } from '../games/discord-data.js';
import { fetchStudioChannelWatchers } from '../games/discord-channels-data.js';
import { useDiscordConnectionRealtime } from '../games/use-discord-connection-realtime.js';
import { useDiscordGuildChannels } from '../games/use-discord-guild-channels.js';

// ---------------------------------------------------------------------------
// Watched-entity resolver for the feedback inbox.
//
// Joins the active game's per-game watched-channel set (table
// game_watched_channels, RPC get_studio_channel_watchers, both added in
// migration 0082) with the guild's mirrored channel list (carrying
// type / parent / name) and returns only the entities the studio
// explicitly opted into for THIS game. Forum channels, individually-
// watched threads, and channels all show up here; threads that emerged
// inside a watched parent forum but were never individually selected do
// NOT - this is the chip source the picker should surface.
//
// Realtime: connection-row changes (status flips, healthcheck etc.) and
// guild channel changes (renames, new threads being mirrored) refetch
// the underlying state. The watched-channel set itself is refreshed on
// the same connection-realtime tick for now; the game_watched_channels
// table has its own NOTIFY trigger we can subscribe to later for tighter
// cross-tab sync.
// ---------------------------------------------------------------------------

export interface UseWatchedEntitiesResult {
  connection: DiscordConnection | null;
  entities: DiscordGuildChannel[];
  loading: boolean;
}

export function useWatchedEntities(gameId: string): UseWatchedEntitiesResult {
  const [connection, setConnection] = useState<DiscordConnection | null>(null);
  const [connectionLoading, setConnectionLoading] = useState<boolean>(true);
  const [watchedChannelIds, setWatchedChannelIds] = useState<string[]>([]);

  const reload = useCallback(async (): Promise<void> => {
    const [next, watchers] = await Promise.all([
      fetchDiscordConnectionForGame(gameId),
      fetchStudioChannelWatchers(gameId),
    ]);
    setConnection(next);
    setWatchedChannelIds(
      watchers.filter((w) => w.thisGameWatches).map((w) => w.discordChannelId),
    );
    setConnectionLoading(false);
  }, [gameId]);

  useEffect(() => {
    setConnectionLoading(true);
    void reload();
  }, [reload]);

  useDiscordConnectionRealtime(connection?.id ?? null, () => {
    void reload();
  });

  const { channels, loading: channelsLoading } = useDiscordGuildChannels(
    connection?.id ?? null,
  );

  const entities = useMemo(() => {
    if (connection === null) return [];
    const watched = new Set(watchedChannelIds);
    return channels.filter((c) => watched.has(c.channelId));
  }, [connection, channels, watchedChannelIds]);

  return {
    connection,
    entities,
    loading: connectionLoading || (connection !== null && channelsLoading),
  };
}
